import { z } from "zod";
import { cardConciergeRequestSchema } from "./customer-cards.js";
import { customerConciergeRequestViewSchema } from "./domain-views.js";
// The structured intake of a concierge request (CHT-749, `docs/architecture/milestone-d-contracts.md` 7.1 and 8, route MD-R20).
// `POST /customer/concierge-requests` takes this body. The customer id is never in it: it is the verified identity's. There is no
// staff field, no status and no owner: a customer cannot set an assignment or a confirmation, whatever the body says (strict).
//
// The allowed values of the closed lists are owned by the domain (`conciergeRequestTypes`, `minimumIntentFieldNames`) and by the
// application (`intakeHints`). This package cannot import them, so `apps/api` has a parity test that compares them.
const id = z.string().min(1).max(64);
export const conciergeIntakeRequestTypeValues = [
    "DINING",
    "VILLA",
    "EVENT",
    "EXPERIENCE",
    "SERVICE",
    "TRANSPORT",
    "OTHER",
];
/** What the app may claim as the origin of a request. `CHAT` is the agent's and `STAFF` is the console's: a customer cannot send them. */
export const conciergeIntakeSourceValues = ["CARD", "ITINERARY_ITEM", "ACTION_CENTER"];
/** The closed codes of a `NEEDS_DETAILS` answer: the fields the type still needs. */
export const conciergeIntakeMissingFieldValues = [
    "dateFrom",
    "dateTo",
    "preferredTime",
    "timeZone",
    "partySize",
    "locationText",
    "specialRequests",
    "details.pickupLocation",
    "details.dropoffLocation",
];
export const conciergeIntakeHintValues = [
    "ASK_FOR_MISSING_DETAILS",
    "DATE_IN_PAST",
    "TIME_ZONE_UNKNOWN",
];
export const conciergeIntakeOutcomeValues = ["SUBMITTED", "NEEDS_DETAILS"];
/**
 * The typed requirements as the app sends them. The shapes are checked again by the domain (`validateRequestRequirements`), which names
 * the field and the rule and never the value. `timeZone` is the zone of the PLACE (an IANA name), never the device's: when a linked
 * item or catalog item has a zone, the server uses that one.
 */
export const conciergeIntakeRequirementsSchema = z.strictObject({
    title: z.string(),
    partySize: z.number().nullish(),
    dateFrom: z.string().nullish(),
    dateTo: z.string().nullish(),
    preferredTime: z.string().nullish(),
    timeZone: z.string().nullish(),
    locationText: z.string().nullish(),
    specialRequests: z.string().nullish(),
    indicativeBudget: z.strictObject({ amountMinor: z.number(), currency: z.string() }).nullish(),
    neededBy: z.iso
        .datetime({ offset: true })
        .transform((value) => new Date(value))
        .nullish(),
    details: z.record(z.string(), z.unknown()).nullish(),
});
// The ids a customer names are ids in the shape of a UUID, as every other route checks them (`idSchema` of the API); a made-up text is a `400`.
const guid = z.guid();
export const conciergeIntakeLinksSchema = z.strictObject({
    tripId: guid.nullish(),
    itineraryItemId: guid.nullish(),
    catalogItemId: guid.nullish(),
});
/** One visible-ASCII key of 1 to 255 characters, the same rule as the `Idempotency-Key` header. */
export const conciergeIntakeIdempotencyKeySchema = z
    .string()
    .min(1)
    .max(255)
    .regex(/^[\x21-\x7e]+$/);
export const createConciergeRequestBodySchema = z.strictObject({
    requestType: z.enum(conciergeIntakeRequestTypeValues),
    conversationId: guid.nullish(),
    requirements: conciergeIntakeRequirementsSchema,
    links: conciergeIntakeLinksSchema.nullish(),
    /** Where the request began. Default `ACTION_CENTER`. `ITINERARY_ITEM` needs `links.itineraryItemId`. */
    source: z.enum(conciergeIntakeSourceValues).nullish(),
    /** `true`: create the request and submit it when its details are complete (the answer is `conciergeIntakeResultSchema`). Default `false`: a DRAFT. */
    createAndSubmit: z.boolean().nullish(),
    /**
     * One key per tap or form. It may come in the `Idempotency-Key` header instead (the app client does that); when both are present
     * they must be equal. The same key and the same body replays; the same key and another body is a `409`.
     */
    idempotencyKey: conciergeIntakeIdempotencyKeySchema.nullish(),
});
/**
 * The answer of `createAndSubmit: true`. `SUBMITTED`: the request is with the concierges. `NEEDS_DETAILS`: it is not; `missingFields` lists
 * what to ask the customer, and `request` is the DRAFT that holds what was given (or `null`: nothing could be stored, for example a date with
 * no zone or a date in the past). Nothing here says that anything is booked.
 */
export const conciergeIntakeResultSchema = z.strictObject({
    outcome: z.enum(conciergeIntakeOutcomeValues),
    requestId: id.nullable(),
    missingFields: z.array(z.enum(conciergeIntakeMissingFieldValues)).max(9),
    hints: z.array(z.enum(conciergeIntakeHintValues)).max(3),
    request: customerConciergeRequestViewSchema.nullable(),
});
/**
 * The same answer as a client reads it: the dates of the request arrive as text on the wire, so the app parses them with the card shape
 * (`cardConciergeRequestSchema`, which `coerce`s them) and the server checks its own answer with `conciergeIntakeResultSchema`.
 */
export const conciergeIntakeWireResultSchema = z.object({
    outcome: z.enum(conciergeIntakeOutcomeValues),
    requestId: id.nullable(),
    missingFields: z.array(z.enum(conciergeIntakeMissingFieldValues)),
    hints: z.array(z.enum(conciergeIntakeHintValues)),
    request: cardConciergeRequestSchema.nullable(),
});
